import { HIDDEN_SELECTION_CLASS_NAME } from '../../utils/table-classes.utils';
import { globalStyle, style, styleVariants } from '@vanilla-extract/css';
import { semanticTokenVars } from '@tundr/ds-tokens';
import {
  borderWidthVars,
  fontFamilyVars,
  fontSizeVars,
  fontWeightVars,
  lineHeightVars,
  radiusVars,
  spacingVars,
} from '@tundr/ds-foundations';

export const ROW_HEIGHT = '68px';

const rowBorder = `${borderWidthVars.xs} solid ${semanticTokenVars.border.neutral.default.subtle}`;
const headerBorder = `${borderWidthVars.xs} solid ${semanticTokenVars.border.neutral.default.idle}`;

export const tableContainerClass = style({});

export const dataTableContainerClass = style({
  borderRadius: radiusVars['2xl'],
  fontSize: fontSizeVars.body.body2,
  overflow: 'hidden',

  vars: {
    '--internal-table-border': rowBorder,
    '--internal-table-row-border': rowBorder,
    '--internal-table-body-row-background-color':
      semanticTokenVars.surface.background.neutral.default.base,
    '--internal-table-header-height': ROW_HEIGHT,
    '--internal-table-header-font-size': fontSizeVars.body.body2,
    '--internal-table-header-font-color':
      semanticTokenVars.content.default.default,
    '--internal-table-header-background-color':
      semanticTokenVars.surface.background.neutral.default.base,
    '--internal-table-header-item-padding': `${spacingVars.md} ${spacingVars.lg}`,
    '--internal-table-header-sort-icon-color':
      semanticTokenVars.surface.background.brand.default.base,
    '--internal-table-header-sort-icon-inactive-color':
      semanticTokenVars.content.muted.default,
    '--internal-table-header-sort-icon-gap': spacingVars.sm,
    '--internal-table-header-tooltip-gap': spacingVars.sm,
    '--internal-table-header-tooltip-icon-color':
      semanticTokenVars.content.muted.default,
    '--internal-table-body-row-height': ROW_HEIGHT,
    '--internal-table-body-row-font-size': fontSizeVars.body.body2,
    '--internal-table-body-row-font-color':
      semanticTokenVars.content.default.default,
    '--internal-table-body-row-hover-background-color':
      semanticTokenVars.surface.background.neutral.default.idle,
    '--internal-table-body-item-padding': `${spacingVars.md} ${spacingVars.lg}`,
    '--internal-table-footer-background-color': 'transparent',
  },
});

globalStyle('.tundr-table .internal-data-table__header th', {
  fontFamily: fontFamilyVars.body,
  fontWeight: fontWeightVars.bold,
  lineHeight: lineHeightVars.body.body2,
  borderBottom: `${headerBorder} !important`,
});

globalStyle('.tundr-table .internal-data-table__body td', {
  fontFamily: fontFamilyVars.body,
  fontWeight: fontWeightVars.regular,
  lineHeight: lineHeightVars.body.body2,
});

globalStyle(
  '.tundr-table .internal-data-table__main.hoverable tr:not(.disabled-row):active td',
  {
    backgroundColor: `${semanticTokenVars.surface.background.neutral.default.raised} !important`,
  },
);

globalStyle('.tundr-table tr.selected-row td', {
  backgroundColor: `${semanticTokenVars.surface.background.neutral.default.raised} !important`,
});

globalStyle('.tundr-table tr.disabled-row td', {
  color: semanticTokenVars.content.disabled.default,
  borderBottom: `${rowBorder} !important`,
});

globalStyle('.tundr-table tr.disabled-row:hover td', {
  backgroundColor: `${semanticTokenVars.surface.background.neutral.default.base} !important`,
  color: `${semanticTokenVars.content.disabled.default} !important`,
});

globalStyle('.tundr-table tr.disabled-row, .tundr-table tr.disabled-row td', {
  cursor: 'default',
});

globalStyle('.tundr-table tr.error-row td', {
  backgroundColor: `${semanticTokenVars.feedback.error.default.subtle} !important`,
  borderBottom: `${rowBorder} !important`,
});

globalStyle(
  `.tundr-table tr.${HIDDEN_SELECTION_CLASS_NAME} .internal-checkbox`,
  {
    display: 'none',
  },
);

globalStyle(
  '.tundr-table.last-row-border .internal-data-table__body tr:last-child td',
  {
    borderBottom: rowBorder,
  },
);

export const centeredContainerClass = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${spacingVars.xl} 0`,
  marginTop: spacingVars['3xl'],
});

export const tableCellClass = style({
  backgroundColor: semanticTokenVars.surface.background.neutral.default.base,
  lineHeight: 1.5,
});

export const tableWithHeaderClass = style({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const tableHeaderWrapperClass = style({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacingVars.sm,
  borderRadius: `${radiusVars.md} ${radiusVars.md} 0 0`,
  borderBottom: 'none',
});

export const tableHeaderWrapperSpacingClass = style({
  marginBottom: spacingVars['2xl'],
});

export const tableHeaderClass = style({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

export const tableTitleClass = style({
  fontSize: fontSizeVars.body.body1,
  fontWeight: fontWeightVars.bold,
  textTransform: 'uppercase',
  marginBottom: spacingVars.sm,
});

export const clickableRowClass = style({
  cursor: 'pointer',
  selectors: {
    '&:focus-visible': {
      outline: `${borderWidthVars.xs} solid ${semanticTokenVars.border.focus.default}`,
      outlineOffset: `calc(${borderWidthVars.xs} * -1)`,
    },
  },
});

export const cellAlignmentVariant = styleVariants({
  left: {
    textAlign: 'left !important' as 'left',
  },
  center: {
    textAlign: 'center !important' as 'center',
  },
  right: {
    textAlign: 'right !important' as 'right',
  },
});

export const tableFooterClass = style({
  marginTop: spacingVars.xs,
  height: ROW_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
